import React, { useState, useEffect, useRef } from 'react';
import { injectIntl } from "react-intl";
import { Link, useRouteMatch, useHistory } from 'react-router-dom';
import TextField from "@FormComponents/TextField";
import stringSanitizer from "stringSanitizer";
import axios from 'axios';
import removeSpacesFromText from "removeSpacesFromText";
import restoreSpacesInText from "restoreSpacesInText";
import { Helmet } from "rnd-helmet";
import searchParamsStringify from "@Utils/searchParamsStringify";
import searchParamsHelper from "@Utils/searchParamsHelper";

function SearchForm({intl, searchParams, specialismOptions, setShowAllResults}) {
  const route = useRouteMatch();
  const history = useHistory();
  const baseUrl = route.path.replace(':searchParams*', '');
  let filterCount =  0;

  let locationFieldValue = '';

  if (searchParams.region || searchParams.department || searchParams.city) {
    let parts = [];

    // First is always region.
    if (searchParams.region) {
      parts.push(restoreSpacesInText(searchParams.region));
    }

    // Second is department.
    if (searchParams.department) {
      parts.push(restoreSpacesInText(searchParams.department));
    }

    // Third is city.
    if (searchParams.city) {
      parts.push(restoreSpacesInText(searchParams.city));
    }

    // {region}, {department}, {city}
    locationFieldValue = parts.join(', ')
  }
  else if (searchParams.postcode) {
    locationFieldValue = restoreSpacesInText(searchParams.postcode);
  }
  else {
    locationFieldValue = '';
  }

  const [locationValueMapped, setLocationValueMapped] = useState(locationFieldValue);
  const [locationValue, setLocationValue] = useState('');
  const [keyPressed, setKeyPressed] = useState(false);

  // SET THE INITIAL LOCATION SEARCH ( based on search params )
  useEffect(() => {
    handleLocationAutocomplete(locationFieldValue);
  }, [])

  useEffect(() => {
    let searchText = locationValue ? locationValue : '';
    handleLocationAutocomplete(searchText);
  }, [locationValue])

  // Update the state with filters ONLY after the locationValueMapped is changed AND NOT BEFORE!
  useEffect(() => {
    let locationOptions = formatLocationSearchData(locationValueMapped);
    setState(prev => {
      return {...prev, ...searchParams, ...locationOptions}
    });
  }, [locationValueMapped])

  const handleLocationAutocomplete = (searchText) => {
    axios.post("/api/branches/get-callback", {
      callback: '@Callbacks/getLocationAutocomplete',
      currentRoute: {},
      data: { searchText: searchText }
    }).then(response => {
      let locationMapping = response.data.locationsMapping ? response.data.locationsMapping : {};
      let locationsMappingSanitised = response.data.locationsMappingSanitised ? response.data.locationsMappingSanitised : {};

      if (searchParams.postcode && Object.values(locationMapping).length) {
        let [ region ] = Object.values(locationMapping).shift().split(', ');
        const params = searchParamsStringify({...searchParams, region: region, ...state});
        window.location = `${baseUrl}${params}`
      }

      let allOptions = response.data.items.length ? response.data.items : Object.keys(locationsMappingSanitised)

      setOptions(allOptions);

      // We go here only when it's coming from the URL then we need to convert it to human readable
      // and trigger the useEffect again with the new value by calling setLocationValue.
      if (typeof locationsMappingSanitised[searchText.replace(/-/g, ' ')] !== "undefined") {
        let locationMappedReadable = locationsMappingSanitised[searchText.replace(/-/g, ' ')]
        locationMapping[locationMappedReadable] = searchText;
        searchText = locationMappedReadable
        setLocationValue(searchText)
      }

      // make the {city} ({postcode}) become {region}/{department}/{city}
      // make the {department} ({department_code}) become {region}/{department}
      // make the {region} become {region}
      let locationMapped = typeof locationMapping[searchText] !== "undefined" ? locationMapping[searchText] : searchText;
      setLocationValueMapped(locationMapped)
    });
  }

  const getAutocompleteOptions = e => {
    const callbackAlias = '@Callbacks/getLocationAutocomplete';
    const data = {
      searchText: e.target.value
    };

    axios.post("/api/branches/get-callback", {
      callback: callbackAlias,
      currentRoute: {},
      data: data
    }).then(response => {
      let locationsMappingSanitised = response.data.locationsMappingSanitised ? response.data.locationsMappingSanitised : {};
      let allOptions = response.data.items.length ? response.data.items : Object.keys(locationsMappingSanitised)
      setOptions(allOptions);

      // Dispatch input event once again, as the event listeners in orbit
      // were already triggered, but the data attributes were not yet ready,
      // because setOptions is a promise and is called after the input event.
      if (typeof document !== 'undefined') {
        // Dispatch the event only if the event is trusted.
        // isTrusted is true only when the event was triggered by the user.
        // It is false when the event was triggered from a script.
        if (e.isTrusted) {
          let event = new CustomEvent('input', {bubbles: true});
          e.target.dispatchEvent(event);
          document.dispatchEvent(event);
        }
      }
    });
  };

  const formatLocationSearchData = (locationValue) => {
    let searchFilters = {};

    // Set defaults.
    searchFilters.region = null;
    searchFilters.department = null;
    searchFilters.city = null;

    // For france it's REGION, DEPARTMENT, CITY
    let locationData = locationValue.split(', ');
    if (locationData.length > 1) {
      searchFilters.region = typeof locationData[0] !== "undefined" ? stringSanitizer(locationData[0]).toLowerCase() : null;
      searchFilters.department = typeof locationData[1] !== "undefined" ? stringSanitizer(locationData[1]).toLowerCase() : null;
      searchFilters.city = typeof locationData[2] !== "undefined" ? stringSanitizer(locationData[2]).toLowerCase() : null;
    }
    else if (locationData.length === 1) {
      let param = stringSanitizer(locationData[0]).toLowerCase();
      if (searchParamsHelper.regionList.includes(param)) {
        searchFilters.region = stringSanitizer(param).toLowerCase()
      }
      else if (Object.values(searchParamsHelper.departmentsList).find(value => stringSanitizer(value.toLowerCase()) === stringSanitizer(encodeURIComponent(param).toLowerCase()))) {
        searchFilters.department = stringSanitizer(param).toLowerCase()
      }
      else {
        searchFilters.city = stringSanitizer(param).toLowerCase()
      }
    }

    return searchFilters;
  };

  if (Object.keys(searchParams).length) {
    const filters = Object.assign({}, searchParams);
    delete filters.type;
    delete filters.page;
    filterCount = Object.keys(filters).length;
  }

  let initialData = {};
  if (searchParams) {
    initialData = searchParams;
  }

  const [options, setOptions] = useState([]);
  const [state, setState] = useState(initialData);
  const autoSuggestRef = useRef();
  const submitRef = useRef();

  useEffect(() => {
    if (typeof window !== "undefined" && window.innerWidth < 941) {
      let searchFilters = {...searchParams, ...state};
      if (state.locationSearch) {
        searchFilters.locationSearch = removeSpacesFromText(state.locationSearch);
      }
      const params = searchParamsStringify(searchFilters);
      history.push(`${baseUrl}${params}`);
    }
  }, [state.distance, state.specialism]);

  useEffect(() => {
    if (autoSuggestRef.current) {
      if (typeof window !== "undefined" && window.innerWidth < 941) {
        autoSuggestRef.current.addEventListener('click', suggestionSelect);
      }
    }
  }, [route.params.searchParams]);

  const suggestionSelect = (event) => {
    let value = event.target.innerHTML;
    if (event.target.localName === "mark") {
      value = event.target.parentElement.innerHTML;
    }

    value = value.replace(new RegExp("<[^>]*>", 'g'), '');
    const params = searchParamsStringify({...searchParams, ...{locationSearch: value}});
    history.push(`${baseUrl}${params}`);
  };

  const locationAutocomplete = (event) => {
    // Keep the event attributes.
    event.persist();
    let value = event.target.value;
    const isFiltered = ['Département','Région'].some(el => value.includes(el));

    if (isFiltered) {
      event.target.value = event.target.value.replaceAll(/Département: |Région: /g, '');
    }

    setLocationValue(event.target.value);
    getAutocompleteOptions(event);
  };

  const onChange = (event) => {
    let name = event.target.name;
    let value = event.target.value;
    if (value === "_none") {
      value = null;
    }

    setState((prev) => {
      return {
        ...prev,
        [name]: value
      }
    });
  };

  const onKeyDown = (event) => {
    event.persist();
    // Submit search on enter key press.
    if (event.keyCode === 13) {
      setKeyPressed(event);
    }
  };

  const pushDataLayerSearchEvent = () => {
    let searchKeyword = '';
    let specialismValue = state.specialism;

    if (state.city) {
      searchKeyword = state.city
    }

    if (!state.city && state.department) {
      searchKeyword = state.department
    }

    if (!state.city && !state.department && state.region) {
      searchKeyword = state.region
    }

    if (!specialismValue || specialismValue === '_none') {
      specialismValue = '';
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "eventTracker",
      eventCategory: "[JE] Branch search",
      eventAction: searchKeyword,
      eventLabel: specialismValue
    });

    window.dataLayer.push({event_params: null});
    window.dataLayer.push({
      'event': 'interaction',
      'event_params' : {
        'event_name': 'branch_search',
        'search_term': specialismValue,
        'search_location': searchKeyword,
        'search_distance': state?.distance,
      }
    });
  }

  const onSubmit = (event) => {
    event.preventDefault();
    // By default, we don't display all results initially - so on click on search - display them.
    setShowAllResults(true);
    pushDataLayerSearchEvent();

    if (searchParams.page) {
      delete searchParams.page;
    }

    setState(prev => {
      delete prev.page
      delete prev.type
      return prev;
    });

    const params = searchParamsStringify({...searchParams, ...{...state, page: null}});

    history.push(`${baseUrl}${params}`);
  };

  useEffect(() => {
    if (((state && state.region) || (state && state.department)) && keyPressed && locationValueMapped) {
      submitRef.current.click();
      setKeyPressed(false);
    }
  },[state, keyPressed, locationValueMapped]);

  const clearFilters = (event) => {
    event.preventDefault();

    setState({});

    let searchParamsCleared = {
      region: null,
      department: null,
      city: null,
      distance: null,
      specialism: null,
      postcode: null,
    };

    const params = searchParamsStringify({...searchParams, ...searchParamsCleared});
    history.push(`${baseUrl}${params}`);
  }

  let specialismFilterOptions = [
    <option key="none" value="_none">{ intl.formatMessage({id: "SearchForm.SpecialismPlaceholder"}) }</option>
  ];
  for (let tid in specialismOptions) {
    let specialismData = specialismOptions[tid];

    specialismFilterOptions.push(
      <option key={specialismData.tid} value={`${stringSanitizer(specialismData.name)}`}>{specialismData.name}</option>
    );
  }

  return(
    <form name="branches-search-form" aria-label="branches-search-form" className="search-form form-groups form-groups--row l:w-3/4">
      <Helmet>
        <script type="text/javascript" src="/branches/assets/js/auto-suggest.js"/>
      </Helmet>

      <div className="form-group search-form__input flex-initial">
        <div className="form-group__input form-group__location form-group__input--button clearable-input" data-rs-clearable={true} data-rs-auto-suggest={true}>
          <TextField name="locationValue"
                     onChange={locationAutocomplete}
                     onSelectItem={locationAutocomplete}
                     onFocus={locationAutocomplete}
                     onKeyDown={onKeyDown}
                     value={locationValue}
                     placeholder={intl.formatMessage({id: "SearchForm.LocationPlaceholder"})}
                     data-rs-auto-suggest-input={true}
                     data-rs-auto-suggest-no-result-message={intl.formatMessage({id: "Autosuggest.Noresults.Message"})}
                     autoComplete="off"/>

          <ul className="select-menu__list" id="geo-search-as" data-rs-use-exact-values={true} data-rs-auto-suggest-list="" data-rs-auto-suggest-values={options.length ? options.splice(0, 40).join(';') : ""} data-rs-auto-suggest-separator=";"/>
        </div>
      </div>

      <div className="form-group search-form__input">
        <div className="form-group__input form-group__location form-group__input--button">
          <select
            className={`${state.specialism ? "" : "untouched"}`}
            aria-label={intl.formatMessage({id: "SearchForm.SpecialismPlaceholder"})}
            value={state.specialism ? state.specialism : "_none"}
            name="specialism"
            onChange={onChange}>
            {specialismFilterOptions}
          </select>
          <span className="select-arrow icon text-brand-primary">
            <svg>
              <use xlinkHref="/themes/custom/bluex/dist/assets/image/icons.svg#chevron-down"/>
            </svg>
          </span>
        </div>
      </div>

      <div className="form-group flex">
        <div className="form-group search-form__input flex-initial w-full">
          <div className="form-group__input form-group__location form-group__input--button">
            <select
              className={`${state.distance ? "" : "untouched"}`}
              aria-label={intl.formatMessage({id: "SearchForm.DistancePlaceholder"})}
              value={!state.city ? "_none" : state.distance ? state.distance : "_none"}
              name="distance"
              onChange={onChange}
              disabled={!state.city}
            >
              <option value="_none">{ intl.formatMessage({id: "SearchForm.DistancePlaceholder"}) }</option>
              <option value="5">{ intl.formatMessage({id: "SearchForm.SearchDistance5"}) }</option>
              <option value="10">{ intl.formatMessage({id: "SearchForm.SearchDistance10"}) }</option>
              <option value="25">{ intl.formatMessage({id: "SearchForm.SearchDistance25"}) }</option>
              <option value="50">{ intl.formatMessage({id: "SearchForm.SearchDistance50"}) }</option>
            </select>
            <span className="select-arrow icon text-brand-primary">
              <svg>
                <use xlinkHref="/themes/custom/bluex/dist/assets/image/icons.svg#chevron-down"/>
              </svg>
            </span>
          </div>
        </div>
        <div className="hidden--from-l">
          <Link to="#" className="button button--m button--filled button--icon my-none ml-xxs" id="sidebar-submit" ref={submitRef} onClick={onSubmit} >
            <span className="button__text">go</span>
            <span className="icon" aria-hidden="true">
              <svg className="fill-current">
                <use xlinkHref="/themes/custom/bluex/dist/assets/image/icons.svg#search"></use>
              </svg>
            </span>
          </Link>
        </div>
      </div>

      <div className="form-group hidden--until-l">
        <Link to="#" className="button button--m button--filled button--off-white" id="sidebar-submit" onClick={onSubmit} aria-label={intl.formatMessage({id: "SearchForm.SearchSubmit.AccessibilityAriaLabel"})}>
          { intl.formatMessage({id: "SearchForm.SearchSubmit"}) }
        </Link>
      </div>
    </form>
  );
}

export default injectIntl(SearchForm);
